import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { z } from "zod";

import { DRAFT_STALE_TIME_MS } from "../constants";
import { isRuleCountable, toCountPayload } from "../mappers";
import {
  countPayloadSchema,
  countResponseSchema,
  type CountPayload,
  type SegmentFormValues,
} from "../schemas";

import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { useDebounce } from "@/lib/hooks/useDebounce";

type UseSegmentCountsParams = {
  clientId?: string;
  values: SegmentFormValues;
  debounceMs: number;
};

export type CountState = {
  count?: number;
  isFetching: boolean;
};

const postCount = async (
  url: string,
  body: object,
  signal: AbortSignal,
): Promise<z.infer<typeof countResponseSchema>> => {
  const response = await instance.post(url, body, { signal });
  return countResponseSchema.parse(response.data);
};

export function useSegmentCounts({
  clientId,
  values,
  debounceMs,
}: UseSegmentCountsParams) {
  const payloadKey = JSON.stringify(toCountPayload(values));
  const debouncedKey = useDebounce(payloadKey, debounceMs);
  const payload: CountPayload = useMemo(
    () => countPayloadSchema.parse(JSON.parse(debouncedKey)),
    [debouncedKey],
  );
  const ruleQueries = useQueries({
    queries: payload.rules.map((rule) => ({
      queryKey: segmentsQueryKeys.ruleCount(clientId, rule),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        postCount(
          "/api/v2/Vacancies/CountByRule",
          { ...rule, clientId2: clientId },
          signal,
        ),
      enabled: isRuleCountable(rule),
      staleTime: DRAFT_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    })),
  });

  const ruleCounts: CountState[] = payload.rules.map((rule, index) => ({
    count: isRuleCountable(rule) ? ruleQueries[index]?.data?.count : undefined,
    isFetching: ruleQueries[index]?.isFetching ?? false,
  }));

  return { ruleCounts };
}
